import {
  queryOptions,
  infiniteQueryOptions,
  keepPreviousData
} from '@tanstack/react-query';
import { devicesApi } from './devices.api';
import type { DevicesListParams } from '../types';
import { STALE_TIME } from '@/lib/query/stale-times';

export const deviceKeys = {
  all: ['devices'] as const,
  lists: () => [...deviceKeys.all, 'list'] as const,
  list: (filters: DevicesListParams) =>
    [...deviceKeys.lists(), filters] as const,
  infiniteLists: () => [...deviceKeys.all, 'infinite'] as const,
  infiniteList: (filters: DevicesListParams) =>
    [...deviceKeys.infiniteLists(), filters] as const,
  details: () => [...deviceKeys.all, 'detail'] as const,
  detail: (id: number) => [...deviceKeys.details(), id] as const,
  metrics: () => [...deviceKeys.all, 'metrics'] as const
};

export const deviceQueries = {
  list: (filters: DevicesListParams = {}) =>
    queryOptions({
      queryKey: deviceKeys.list(filters),
      queryFn: () => devicesApi.getAll(filters),
      placeholderData: keepPreviousData
    }),

  /**
   * Page-cursor infinite variant for scroll-to-load surfaces (e.g. the
   * scanner-selection card grid). `filters` must not include `page` — the
   * cursor owns it.
   */
  infiniteList: (filters: DevicesListParams = {}) =>
    infiniteQueryOptions({
      queryKey: deviceKeys.infiniteList(filters),
      queryFn: ({ pageParam }) =>
        devicesApi.getAll({ ...filters, page: pageParam }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.hasNext ? lastPage.pagination.page + 1 : undefined,
      placeholderData: keepPreviousData
    }),

  detail: (id: number) =>
    queryOptions({
      queryKey: deviceKeys.detail(id),
      queryFn: () => devicesApi.getById(id),
      enabled: id > 0
    }),

  metrics: () =>
    queryOptions({
      queryKey: deviceKeys.metrics(),
      queryFn: () => devicesApi.metrics(),
      staleTime: STALE_TIME.metrics
    })
};
